import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Box, Radio, RadioGroup, TextField, CircularProgress, CardContent, Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ErrorOutline,
  LockOutlined,
  LocalShippingOutlined,
} from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import Footer from '../../molecule/footer/footer';
import Reveal from '../../atome/motion/reveal';
import { Eyebrow } from '../../atome/motion/motion.styled';
import { RootState } from '../../../redux';
import { clearCart, setLineQuantity } from '../../../redux/cart';
import { CreateOrderPayload, OrderServices } from '../../../api/order';
import { unitPriceFor } from '../../../api/product';
import { resolveImage } from '../../../api/axiosIntance';
import { color } from '../../../theme/tokens';
import {
  CheckoutContainer, CheckoutHead, CheckoutSubtitle, CheckoutTitle, EmptyCart, ErrorBanner,
  MethodHint, OrderSummaryCard, PaymentMethodLabel, ProductImage, ProductRow, SectionHeader,
  SectionNumber, SectionTitle, Step, StepLine, StepRail, StyledPaper, SubmitButton,
  SummaryNote, SummaryQty, SummaryRow, TotalRow, CheckoutWrapper,
} from './checkout.styled';

const FALLBACK_IMAGE = '/img/klarity-product.jpg';

/**
 * Shared look for every input on the page.
 *
 * The cards behind these fields are near-white, so a white field would only be
 * outlined by its border — which reads as empty space on the tall address box.
 * A warm cream fill marks out where you type, and focus lifts it to solid white.
 */
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: 'rgba(242, 236, 229, 0.55)',
    transition: 'box-shadow 240ms ease, background-color 240ms ease',
    '& fieldset': { borderColor: 'rgba(74, 112, 97, 0.2)' },
    '&:hover': { backgroundColor: 'rgba(242, 236, 229, 0.8)' },
    '&:hover fieldset': { borderColor: color.forestSoft },
    '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 8px 22px rgba(74,112,97,0.12)' },
    '&.Mui-focused fieldset': { borderColor: color.forest, borderWidth: '1px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: color.forest },
};

const validationSchema = Yup.object().shape({
  customerInfo: Yup.object().shape({
    customer_email: Yup.string().email('Invalid email'),
    first_name: Yup.string().trim().required('First name is required'),
    last_name: Yup.string().trim().required('Last name is required'),
    phone_number: Yup.string()
      .trim()
      .min(8, 'Phone number must be at least 8 characters')
      .required('Phone number is required'),
  }),
  addressInfo: Yup.object().shape({
    city: Yup.string().trim().required('City is required'),
    state: Yup.string().trim().required('State/Region is required'),
    address_description: Yup.string().trim().required('Address description is required'),
  }),
  paymentInfo: Yup.object().shape({
    payment_provider: Yup.string().required('Payment method is required'),
  }),
});

const Checkout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const lines = useSelector((state: RootState) => state.cart.lines);
  const email = useSelector((state: RootState) => state.user?.email) || '';

  const [submitError, setSubmitError] = useState('');

  // Bag emptied, or the visitor deep-linked into checkout.
  if (lines.length === 0) {
    return (
      <CheckoutWrapper>
        <CheckoutContainer maxWidth="lg">
          <EmptyCart>
            <h3>Your bag is empty</h3>
            <p>Pick a product first and it will show up here, ready to check out.</p>
            <SubmitButton style={{ maxWidth: '16rem', margin: '0 auto' }} onClick={() => navigate('/')}>
              Back to the shop
            </SubmitButton>
          </EmptyCart>
        </CheckoutContainer>
        <Footer />
      </CheckoutWrapper>
    );
  }

  const changeQuantity = (productId: number, quantity: number) =>
    dispatch(setLineQuantity({ productId, quantity }));

  const handleSubmit = async (values: any) => {
    setSubmitError('');
    const payload: CreateOrderPayload = {
      customerInfo: {
        // Always send the key — the API rejects an undefined bind parameter.
        customer_email: values.customerInfo.customer_email?.trim() || null,
        first_name: values.customerInfo.first_name.trim(),
        last_name: values.customerInfo.last_name.trim(),
        phone_number: values.customerInfo.phone_number.trim(),
      },
      addressInfo: {
        city: values.addressInfo.city.trim(),
        state: values.addressInfo.state.trim(),
        address_description: values.addressInfo.address_description.trim(),
      },
      paymentInfo: { payment_provider: values.paymentInfo.payment_provider },
      // The API prices every line itself and guards each one against stock.
      items: lines.map((line) => ({
        product_id: Number(line.product.id),
        quantity: line.quantity,
      })),
    };

    try {
      const response = await OrderServices.createOrder(payload);
      dispatch(clearCart());
      navigate('/order-success', { state: { orderResponse: response } });
    } catch (error: any) {
      setSubmitError(error?.message || 'Something went wrong. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const initialValues = {
    customerInfo: { customer_email: email, first_name: '', last_name: '', phone_number: '' },
    addressInfo: { city: '', state: '', address_description: '' },
    paymentInfo: { payment_provider: 'delivery' as const },
  };

  return (
    <CheckoutWrapper>
      <CheckoutContainer maxWidth="lg">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => {
            const contactDone = Boolean(
              values.customerInfo.first_name &&
                values.customerInfo.last_name &&
                values.customerInfo.phone_number.length >= 8
            );
            const addressDone = Boolean(
              values.addressInfo.city &&
                values.addressInfo.state &&
                values.addressInfo.address_description
            );

            // Mirrors the API: an explicit product discount wins, otherwise an
            // identified customer gets 10% off. The two never stack.
            const hasEmail = Boolean(values.customerInfo.customer_email?.trim());
            const subtotal = lines.reduce((sum, l) => sum + l.product.price * l.quantity, 0);
            const total = lines.reduce(
              (sum, l) => sum + unitPriceFor(l.product, hasEmail) * l.quantity,
              0
            );
            const savings = subtotal - total;
            const itemCount = lines.reduce((sum, l) => sum + l.quantity, 0);

            return (
              <Form>
                <Reveal from="up">
                  <CheckoutHead>
                    <Eyebrow>Almost yours</Eyebrow>
                    <CheckoutTitle variant="h1">Checkout</CheckoutTitle>
                    <CheckoutSubtitle>
                      Free delivery across Lebanon · pay when it arrives
                    </CheckoutSubtitle>

                    <StepRail>
                      <Step $active>
                        <b>1</b> Contact
                      </Step>
                      <StepLine />
                      <Step $active={contactDone}>
                        <b>2</b> Delivery
                      </Step>
                      <StepLine />
                      <Step $active={contactDone && addressDone}>
                        <b>3</b> Payment
                      </Step>
                    </StepRail>
                  </CheckoutHead>
                </Reveal>

                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, md: 8 }}>
                    {submitError && (
                      <ErrorBanner key={submitError}>
                        <ErrorOutline fontSize="small" />
                        {submitError}
                      </ErrorBanner>
                    )}

                    <Reveal from="up" delay={60}>
                      <StyledPaper elevation={0}>
                        <SectionHeader>
                          <SectionNumber>1</SectionNumber>
                          <Box>
                            <SectionTitle variant="h2">Contact information</SectionTitle>
                            <span>So we know where to send the confirmation</span>
                          </Box>
                        </SectionHeader>

                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth sx={fieldSx}
                              name="customerInfo.first_name"
                              label="First name"
                              variant="outlined"
                              value={values.customerInfo.first_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.customerInfo?.first_name && Boolean(errors?.customerInfo?.first_name)}
                              helperText={touched.customerInfo?.first_name && errors.customerInfo?.first_name}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth sx={fieldSx}
                              name="customerInfo.last_name"
                              label="Last name"
                              variant="outlined"
                              value={values.customerInfo.last_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.customerInfo?.last_name && Boolean(errors?.customerInfo?.last_name)}
                              helperText={touched.customerInfo?.last_name && errors.customerInfo?.last_name}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth sx={fieldSx}
                              name="customerInfo.customer_email"
                              label="Email"
                              variant="outlined"
                              value={values.customerInfo.customer_email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.customerInfo?.customer_email && Boolean(errors?.customerInfo?.customer_email)}
                              helperText={
                                (touched.customerInfo?.customer_email && errors.customerInfo?.customer_email) ||
                                'Optional — adds 10% off your order'
                              }
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth sx={fieldSx}
                              name="customerInfo.phone_number"
                              label="Phone number"
                              variant="outlined"
                              value={values.customerInfo.phone_number}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.customerInfo?.phone_number && Boolean(errors?.customerInfo?.phone_number)}
                              helperText={touched?.customerInfo?.phone_number && errors?.customerInfo?.phone_number}
                            />
                          </Grid>
                        </Grid>
                      </StyledPaper>
                    </Reveal>

                    <Reveal from="up" delay={120}>
                      <StyledPaper elevation={0}>
                        <SectionHeader>
                          <SectionNumber>2</SectionNumber>
                          <Box>
                            <SectionTitle variant="h2">Delivery address</SectionTitle>
                            <span>Add a landmark — it helps our driver</span>
                          </Box>
                        </SectionHeader>

                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth sx={fieldSx}
                              name="addressInfo.city"
                              label="City"
                              variant="outlined"
                              value={values.addressInfo.city}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.addressInfo?.city && Boolean(errors?.addressInfo?.city)}
                              helperText={touched?.addressInfo?.city && errors?.addressInfo?.city}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth sx={fieldSx}
                              name="addressInfo.state"
                              label="State / region"
                              variant="outlined"
                              value={values.addressInfo.state}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.addressInfo?.state && Boolean(errors?.addressInfo?.state)}
                              helperText={touched?.addressInfo?.state && errors?.addressInfo?.state}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth multiline minRows={2} sx={fieldSx}
                              name="addressInfo.address_description"
                              label="Street, building, floor"
                              variant="outlined"
                              value={values.addressInfo.address_description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.addressInfo?.address_description && Boolean(errors?.addressInfo?.address_description)}
                              helperText={touched?.addressInfo?.address_description && errors?.addressInfo?.address_description}
                            />
                          </Grid>
                        </Grid>
                      </StyledPaper>
                    </Reveal>

                    <Reveal from="up" delay={180}>
                      <StyledPaper elevation={0}>
                        <SectionHeader>
                          <SectionNumber>3</SectionNumber>
                          <Box>
                            <SectionTitle variant="h2">Payment method</SectionTitle>
                            <span>No card needed today</span>
                          </Box>
                        </SectionHeader>

                        <RadioGroup
                          name="paymentInfo.payment_provider"
                          value={values.paymentInfo.payment_provider}
                          onChange={handleChange}
                        >
                          <PaymentMethodLabel
                            value="delivery"
                            control={<Radio sx={{ color: color.sandDeep, '&.Mui-checked': { color: color.forest } }} />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocalShippingOutlined sx={{ color: color.forest, fontSize: 20 }} />
                                <Box>
                                  Cash on delivery
                                  <MethodHint>Pay the driver when your order arrives.</MethodHint>
                                </Box>
                              </Box>
                            }
                          />

                          {/* Whish Money and bank transfer are wired on the API
                              but not configured, so they are hidden rather than
                              shown as "Soon". Cash on delivery is the only
                              method, and stays the default.

                          <PaymentMethodLabel
                            $disabled
                            value="wishmoney"
                            disabled
                            control={<Radio disabled />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalanceWalletOutlined sx={{ color: color.inkFaint, fontSize: 20 }} />
                                <Box>
                                  Whish Money <SoonTag>Soon</SoonTag>
                                  <MethodHint>Online payment is being set up.</MethodHint>
                                </Box>
                              </Box>
                            }
                          />
                          <PaymentMethodLabel
                            $disabled
                            value="bank"
                            disabled
                            control={<Radio disabled />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalanceOutlined sx={{ color: color.inkFaint, fontSize: 20 }} />
                                <Box>
                                  Bank transfer <SoonTag>Soon</SoonTag>
                                  <MethodHint>Ask us for the account details.</MethodHint>
                                </Box>
                              </Box>
                            }
                          />

                              Restoring them also means putting back the
                              AccountBalanceOutlined / AccountBalanceWalletOutlined
                              icon imports and SoonTag. */}
                        </RadioGroup>
                      </StyledPaper>
                    </Reveal>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Reveal from="right" delay={100}>
                      <OrderSummaryCard elevation={0}>
                        <CardContent sx={{ p: 3 }}>
                          <SectionTitle variant="h2" sx={{ mb: 2 }}>
                            Order summary
                          </SectionTitle>

                          {lines.map(({ product, quantity }) => {
                            const unit = unitPriceFor(product, hasEmail);
                            const image = resolveImage(product.imageUrl?.[0]) || FALLBACK_IMAGE;

                            return (
                              <ProductRow key={product.id}>
                                <ProductImage
                                  src={image}
                                  alt={product.name}
                                  onError={(e) => {
                                    e.currentTarget.src = FALLBACK_IMAGE;
                                  }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                                    {product.name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: color.inkSoft }}>
                                    ${unit.toFixed(2)} each
                                  </Typography>
                                  <SummaryQty>
                                    <button
                                      type="button"
                                      onClick={() => changeQuantity(product.id, quantity - 1)}
                                      aria-label={`Less ${product.name}`}
                                    >
                                      <RemoveIcon sx={{ fontSize: 14 }} />
                                    </button>
                                    <span>{quantity}</span>
                                    <button
                                      type="button"
                                      onClick={() => changeQuantity(product.id, quantity + 1)}
                                      disabled={quantity >= product.stock}
                                      aria-label={`More ${product.name}`}
                                    >
                                      <AddIcon sx={{ fontSize: 14 }} />
                                    </button>
                                  </SummaryQty>
                                </Box>
                                <Typography sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                                  ${(unit * quantity).toFixed(2)}
                                </Typography>
                              </ProductRow>
                            );
                          })}

                          <SummaryRow>
                            <Typography variant="body2">
                              Subtotal · {itemCount} {itemCount === 1 ? 'item' : 'items'}
                            </Typography>
                            <Typography variant="body2">${subtotal.toFixed(2)}</Typography>
                          </SummaryRow>

                          <SummaryRow>
                            <Typography variant="body2">Delivery</Typography>
                            <Typography variant="body2" sx={{ color: color.forest }}>
                              Free
                            </Typography>
                          </SummaryRow>

                          <SummaryRow>
                            <Typography variant="body2">Discount</Typography>
                            <Typography variant="body2" sx={{ color: savings > 0 ? color.forest : color.inkFaint }}>
                              {savings > 0 ? `−$${savings.toFixed(2)}` : '$0.00'}
                            </Typography>
                          </SummaryRow>

                          <TotalRow>
                            <span>Total</span>
                            <span>${total.toFixed(2)}</span>
                          </TotalRow>

                          <Box sx={{ mt: 2.5 }}>
                            <SubmitButton type="submit" disabled={isSubmitting}>
                              {isSubmitting ? (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                                  Placing your order…
                                </Box>
                              ) : (
                                'Complete order'
                              )}
                            </SubmitButton>
                          </Box>

                          <SummaryNote>
                            <LockOutlined />
                            {hasEmail
                              ? 'Your 10% email discount is applied. We only use your details for this delivery.'
                              : 'Add your email above to take 10% off. We only use your details for this delivery.'}
                          </SummaryNote>
                        </CardContent>
                      </OrderSummaryCard>
                    </Reveal>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </CheckoutContainer>

      <Footer />
    </CheckoutWrapper>
  );
};

export default Checkout;
